import React, { useCallback, useState } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  Button,
  Chip,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { CalendarMonth, CheckCircle, ContentCopy, Link, Close, PlayCircleOutline, InfoOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import CustomInput from "../../components/CustomInput";
import InstructionStep from "../../components/InstructionStep";
import GoogleCalendarConfig from "./GoogleCalendarConfig";

import { calendarProviders, providerExamples, providerPricing, providerPlaceholders, providerInstructions, TEAM_SCHEDULING_PROVIDER } from "../../utils/providerConfig";
import { getLoomEmbedUrl } from "../../utils/calendarUtils";
import { useSystemTheme } from "context/SystemThemeContext";

const SingleCalendar = ({
  domainId,
  singleProvider,
  googleStatus,
  googleOAuth,
  snackbar,
  setSnackbar,
  confirmDialog,
  setConfirmDialog,
  showVideo,
  setShowVideo,
  currentVideoUrl,
  setCurrentVideoUrl,
  urlError,
  setUrlError,
  showPopupHelp,
  setShowPopupHelp,
  isRemovingAccount,
  isMobile,
  LOOM_SHARE_URLS,
  validateUrl,
  handleSaveProvider,
  handleConnectGoogle,
  handleSaveGoogleCalendarProvider,
  handleRemoveGoogleAccount
}) => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const [selectOpen, setSelectOpen] = useState(false);

  const isContactFormSelected = singleProvider.selectedProvider === "contact-form";
  const isGoogleCalendarSelected = singleProvider.selectedProvider === "google-calendar";
  const hasVideo = calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.hasVideo && LOOM_SHARE_URLS[singleProvider.selectedProvider];

  const handleProviderChange = useCallback((event) => {
    const providerId = event.target.value;
    singleProvider.setSelectedProvider(providerId);
    setUrlError("");
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, [singleProvider, setUrlError, setShowVideo, setCurrentVideoUrl]);

  const handleUrlChange = useCallback((e) => {
    const value = e.target.value;
    singleProvider.setCalendarUrl(value);
    if (singleProvider.selectedProvider && singleProvider.selectedProvider !== "google-calendar") {
      const error = validateUrl(singleProvider.selectedProvider, value);
      setUrlError(error);
    }
  }, [singleProvider.selectedProvider, validateUrl, singleProvider, setUrlError]);

  const openProviderWebsite = useCallback(() => {
    const urls = {
      "google-calendar": "https://calendar.google.com/calendar/u/0/r",
      "calendly": "https://calendly.com",
      "zoho-calendar": "https://calendar.zoho.com",
    };
    if (singleProvider.selectedProvider && urls[singleProvider.selectedProvider]) {
      window.open(urls[singleProvider.selectedProvider], "_blank");
    }
  }, [singleProvider.selectedProvider]);

  const handleWatchVideo = useCallback(() => {
    const embedUrl = getLoomEmbedUrl(LOOM_SHARE_URLS[singleProvider.selectedProvider]);
    if (embedUrl) {
      setCurrentVideoUrl(embedUrl);
      setShowVideo(true);
      
      if (isMobile) {
        setTimeout(() => {
          document.querySelector('.video-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [singleProvider.selectedProvider, LOOM_SHARE_URLS, setCurrentVideoUrl, setShowVideo, isMobile]);

  const renderGoogleCalendarUI = () => {
    if (!googleStatus.hasGoogleCredentials) {
      return (
        <Box>
          <Paper sx={{ 
            p: 3, 
            mb: 2,
            backgroundColor: isDarkMode ? '#0a1020' : '#f0f7fa',
            borderRadius: '8px',
            border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0f0f0'}`,
            transition: 'background-color 0.3s ease, border-color 0.3s ease',
          }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: '#219297',
                }}
              >
                <CalendarMonth />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ color: '#219297' }}>
                  Google Calendar Connection Required
                </Typography>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#a0aec0' : '#666' }}>
                  Connect your Google Calendar to configure custom working hours
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              fullWidth
              onClick={handleConnectGoogle}
              disabled={googleOAuth.oauthLoading || googleStatus.isLoading}
              startIcon={googleOAuth.oauthLoading ? <CircularProgress size={16} /> : <CalendarMonth />}
              sx={{
                backgroundColor: '#219297',
                color: '#ffffff',
                fontWeight: 600,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#197f85',
                }
              }}
            >
              {googleOAuth.oauthLoading ? 'Connecting...' : 'Connect Google Calendar'}
            </Button>
          </Paper>
        </Box>
      );
    } else {
      return (
        <Box>
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: isDarkMode ? '#0d1425' : '#e8f5e9', 
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            transition: 'background-color 0.3s ease',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: '#2e7d32' }} />
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                Google Calendar is connected
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setConfirmDialog({ 
                open: true, 
                action: "remove-account",
                data: { provider: "google-calendar" }
              })}
              disabled={isRemovingAccount}
              sx={{
                borderColor: "#d32f2f",
                color: "#d32f2f",
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#ffebee",
                  borderColor: "#b71c1c",
                },
              }}
            >
              Remove Account
            </Button>
          </Box>
          
          <GoogleCalendarConfig 
            domainId={domainId}
            onConfigSaved={(savedConfig) => {
              setSnackbar({
                open: true,
                severity: "success",
                message: "Google Calendar configuration saved successfully!",
              });
              googleStatus.setSingleCalendarStatus(prev => ({
                ...prev,
                config: savedConfig,
                is_configured: true,
                can_accept_bookings: savedConfig.enabled
              }));
            }}
            initialStatus={googleStatus.singleCalendarStatus}
          />
        </Box>
      );
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 1.5, 
              color: '#219297',
              fontWeight: 600,
              fontSize: { xs: '0.9rem', md: '0.95rem' }
            }}
          >
            Select Calendar Provider
          </Typography>
          
          <FormControl fullWidth>
            <Box sx={{ position: 'relative' }}>
              <Select
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => setSelectOpen(false)}
                value={singleProvider.selectedProvider}
                onChange={handleProviderChange}
                disabled={singleProvider.loading.fetch || singleProvider.loading.check}
                IconComponent={() => null}
                fullWidth
                displayEmpty
                sx={{
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#0a1020 !important' : '#f8fafa',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e0e0e0'}`,
                  '& .MuiSelect-select': {
                    backgroundColor: isDarkMode ? '#0a1020 !important' : '#f8fafa',
                    color: isDarkMode ? '#e2e8f0 !important' : undefined,

                  },
                  '&:hover': {
                    borderColor: '#219297',
                  },
                  '&.Mui-focused': {
                    borderColor: '#219297',
                    boxShadow: '0 0 0 3px rgba(33, 146, 151, 0.1)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                     
                      backgroundColor: isDarkMode ? '#060C18' : '#fff',
                      boxShadow: isDarkMode ? '0px 4px 16px rgba(0,0,0,0.3)' : '0px 4px 16px rgba(0,0,0,0.08)',
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        backgroundColor: isDarkMode ? '#060C18 !important' : '#fff !important',
                        color: isDarkMode ? '#e2e8f0 !important' : undefined,
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#2d2d3f !important' : '#f5f5f5 !important',
                        },
                        '&.Mui-selected': {
                          backgroundColor: isDarkMode ? '#21929720 !important' : '#e8f5e9 !important',
                          color: isDarkMode ? '#219297 !important' : undefined,
                        },
                      },
                    },
                  },
                }}
                renderValue={(selected) => {
                  const provider = calendarProviders.find(p => p.id === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={provider?.logo}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: `${provider?.color || '#219297'}15`,
                          color: provider?.color || '#219297',
                        }}
                      >
                        {provider?.icon}
                      </Avatar>
                      <Typography 
                        variant="body1" 
                        fontWeight="medium" 
                        sx={{ 
                          color: isDarkMode ? '#e2e8f0 !important' : 'text.primary',
                        }}
                      >
                        {provider?.name || selected}
                      </Typography>
                    </Box>
                  );
                }}
              >
                {calendarProviders
                  .filter(p => p.type === "single-user")
                  .map((provider) => (
                    <MenuItem 
                      key={provider.id} 
                      value={provider.id}
                      sx={{
                        backgroundColor: isDarkMode ? '#060C18 !important' : '#fff !important',
                        color: isDarkMode ? '#e2e8f0 !important' : undefined,
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#2d2d3f !important' : '#f5f5f5 !important',
                        },
                        '&.Mui-selected': {
                          backgroundColor: isDarkMode ? '#21929720 !important' : '#e8f5e9 !important',
                          color: isDarkMode ? '#219297 !important' : undefined,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Avatar
                          src={provider.logo}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: `${provider.color}15`,
                            color: provider.color,
                          }}
                        >
                          {provider.icon}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight="medium" 
                            sx={{ 
                              color: 'inherit !important',
                            }}
                          >
                            {provider.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: isDarkMode ? '#a0aec0 !important' : 'text.secondary',
                            }}
                            noWrap
                          >
                            {provider.description}
                          </Typography>
                        </Box>
                        {singleProvider.hasExistingConnection && singleProvider.existingProvider === provider.id && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                            {provider.id === "google-calendar" && googleStatus.singleCalendarStatus?.is_configured && (
                              <Chip
                                label="Configured"
                                size="small"
                                sx={{ 
                                  ml: 1,
                                  fontSize: '0.6rem',
                                  height: '16px',
                                  backgroundColor: '#2e7d3215',
                                  color: '#2e7d32'
                                }}
                              />
                            )}
                          </Box>
                        )}
                        {singleProvider.loading.fetch && singleProvider.selectedProvider === provider.id && (
                          <CircularProgress size={16} sx={{ color: '#219297' }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
              <ExpandMore
                onClick={() => {
                  if (!singleProvider.loading.fetch && !singleProvider.loading.check) {
                    setSelectOpen(true);
                  }
                }}
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: selectOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                  color: isDarkMode ? '#a0aec0' : 'text.secondary',
                  fontSize: 20,
                  cursor: singleProvider.loading.fetch || singleProvider.loading.check ? 'default' : 'pointer',
                  zIndex: 1,
                  transition: 'transform 0.2s ease',
                  pointerEvents: singleProvider.loading.fetch || singleProvider.loading.check ? 'none' : 'all',
                }}
              />
            </Box>
          </FormControl>
        </Box>

        {singleProvider.selectedProvider && singleProvider.selectedProvider !== TEAM_SCHEDULING_PROVIDER && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#219297',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', md: '0.95rem' }
                }}
              >
                {singleProvider.selectedProvider === "google-calendar" ? "Google Calendar Configuration" : 
                 providerPlaceholders[singleProvider.selectedProvider]}
              </Typography>
              
              {!isContactFormSelected && singleProvider.selectedProvider !== "google-calendar" && (
                <Button
                  startIcon={<CalendarMonth />}
                  onClick={openProviderWebsite}
                  size="small"
                  sx={{
                    color: '#219297',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#2d2d3f' : '#e0faf3ff',
                      color: '#1a767a',
                    },
                  }}
                >
                  Open {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name || 'Provider'}
                </Button>
              )}
            </Box>

            {isGoogleCalendarSelected ? (
              <Box>
                {renderGoogleCalendarUI()}
              </Box>
            ) : (
              <Box>
                <CustomInput
                  value={singleProvider.calendarUrl}
                  onChange={handleUrlChange}
                  placeholder={providerPlaceholders[singleProvider.selectedProvider] || "Enter your calendar URL"}
                  error={!!urlError}
                  helperText={urlError || (isContactFormSelected 
                    ? "Enter the URL of your contact page or form" 
                    : "Make sure the URL is publicly accessible")}
                  disabled={singleProvider.loading.save || singleProvider.loading.fetch}
                  startIcon={<Link sx={{ color: '#219297' }} />}
                  endAdornment={
                    singleProvider.calendarUrl && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => singleProvider.setCalendarUrl("")}
                          sx={{ p: 0.5 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                        <Tooltip title="Copy URL">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(singleProvider.calendarUrl);
                              setSnackbar({
                                open: true,
                                severity: "success",
                                message: "URL copied to clipboard!",
                              });
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )
                  }
                />

                <Box sx={{ 
                  mt: 1, 
                  p: 1.5, 
                  backgroundColor: isDarkMode ? '#0d1425' : '#e3f2fd', 
                  borderRadius: '8px',
                  border: `1px solid ${isDarkMode ? '#2d2d3f' : '#bbdefb'}`,
                  transition: 'background-color 0.3s ease, border-color 0.3s ease',
                }}>
                  <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Example URL:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#0d47a1', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {providerExamples[singleProvider.selectedProvider]}
                  </Typography>
                </Box>

                {providerPricing[singleProvider.selectedProvider] && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlined sx={{ color: isDarkMode ? '#a0aec0' : '#666', fontSize: 16 }} />
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666' }}>
                      Pricing: {providerPricing[singleProvider.selectedProvider]}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          {singleProvider.hasExistingConnection && singleProvider.existingProvider !== TEAM_SCHEDULING_PROVIDER ? (
            <>
              {isGoogleCalendarSelected ? (
                <>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSaveGoogleCalendarProvider}
                    disabled={singleProvider.loading.save || googleStatus.isLoading}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: isDarkMode ? '#2d2d3f' : '#e0e0e0',
                        color: isDarkMode ? '#a0aec0' : '#999',
                      },
                    }}
                  >
                    {singleProvider.loading.save ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : googleStatus.singleCalendarStatus?.is_configured ? (
                      "Update Configuration"
                    ) : (
                      "Save Configuration"
                    )}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                    disabled={singleProvider.loading.disconnect}
                    sx={{
                      borderColor: "#d32f2f",
                      color: "#d32f2f",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "rgb(202, 25, 52)",
                        borderColor: "#b71c1c",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {singleProvider.loading.disconnect ? "Disconnecting..." : "Disconnect"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSaveProvider}
                    disabled={singleProvider.loading.save || !singleProvider.calendarUrl.trim() || !!urlError}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: isDarkMode ? '#2d2d3f' : '#e0e0e0',
                        color: isDarkMode ? '#a0aec0' : '#999',
                      },
                    }}
                  >
                    {singleProvider.loading.save ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : singleProvider.existingProvider !== singleProvider.selectedProvider ? (
                      "Update Connection"
                    ) : (
                      "Update URL"
                    )}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                    disabled={singleProvider.loading.disconnect}
                    sx={{
                      borderColor: "#d32f2f",
                      color: "#d32f2f",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "rgb(202, 25, 52)",
                        borderColor: "#b71c1c",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {singleProvider.loading.disconnect ? "Disconnecting..." : "Disconnect"}
                  </Button>
                </>
              )}
            </>
          ) : singleProvider.selectedProvider === TEAM_SCHEDULING_PROVIDER ? (
            <Button
              variant="contained"
              fullWidth
              onClick={() => {}}
              sx={{
                backgroundColor: "#219297",
                fontWeight: 600,
                textTransform: "none",
                color: "#fff",
                borderRadius: "8px",
                padding: { xs: "10px 16px", md: "12px 24px" },
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
              }}
            >
              Go to Team Scheduling
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveProvider}
              disabled={singleProvider.loading.save || (singleProvider.selectedProvider !== "google-calendar" && !singleProvider.calendarUrl.trim()) || !!urlError}
              sx={{
                backgroundColor: "#219297",
                fontWeight: 600,
                textTransform: "none",
                color: "#fff",
                borderRadius: "8px",
                padding: { xs: "10px 16px", md: "12px 24px" },
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
                "&.Mui-disabled": {
                  backgroundColor: isDarkMode ? '#2d2d3f' : '#e0e0e0',
                  color: isDarkMode ? '#a0aec0' : '#999',
                },
              }}
            >
              {singleProvider.loading.save ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                `Enable ${calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name || 'Provider'}`
              )}
            </Button>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        {singleProvider.selectedProvider && singleProvider.selectedProvider !== TEAM_SCHEDULING_PROVIDER && (
          <Paper
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: '12px',
              backgroundColor: isDarkMode ? '#0a1020' : '#f8fafa',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0f0f0'}`,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                src={calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.logo}
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: `${calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.color || '#219297'}15`,
                  color: calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.color || '#219297',
                }}
              >
                {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.icon}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#a0aec0' : '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                  {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.description}
                </Typography>
              </Box>
            </Box>
            {hasVideo && (
              <Box className="video-section" sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 1.5 
                }}>
                  <Button
                    startIcon={<PlayCircleOutline />}
                    onClick={handleWatchVideo}
                    size="small"
                    sx={{
                      color: '#219297',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#2d2d3f' : '#e0f7fa',
                      },
                    }}
                  >
                    Watch Guide
                  </Button>
                </Box>
                
                <Collapse in={showVideo}>
                  <Box sx={{ 
                    position: 'relative', 
                    width: '100%', 
                    pt: '56.25%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    mb: 1.5
                  }}>
                    {currentVideoUrl && (
                      <iframe
                        src={currentVideoUrl}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Setup Guide Video"
                      />
                    )}
                  </Box>
                </Collapse>
                
                <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', display: 'block' }}>
                  Watch this short guide to learn how to set up your {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name} integration.
                </Typography>
              </Box>
            )}

            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 2, 
                  color: '#219297',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <InfoOutlined fontSize="small" />
                Setup Instructions
              </Typography>
              
              <Box sx={{ 
                backgroundColor: isDarkMode ? '#060C18' : '#fff', 
                borderRadius: '8px', 
                p: 2.5,
                border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0f0f0'}`,
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
              }}>
                {providerInstructions[singleProvider.selectedProvider]?.map((step, index) => (
                  <InstructionStep
                    key={index}
                    number={index + 1}
                    title={step.title}
                  >
                    {step.content}
                  </InstructionStep>
                ))}

                {isContactFormSelected && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 1.5, 
                    backgroundColor: isDarkMode ? '#2d2d3f' : '#fff3e0', 
                    borderRadius: '6px',
                    border: `1px solid ${isDarkMode ? '#4a5568' : '#ffcc80'}`,
                    transition: 'background-color 0.3s ease, border-color 0.3s ease',
                  }}>
                    <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Important:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#e65100' }}>
                      Your contact form should include fields for name, email, and message. 
                      Ansera will monitor this page for new inquiries and notify you.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default SingleCalendar;